import Footer from "@components/layout/footer";
import { loadTranslation } from "@lib/utils";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { t } from "@lingui/macro";
import Link from "next/link";
import { useRouter } from "next/router";
import Image from "next/image";
import WrappedMobileBg from "@public/wrapped-mobile-bg.png";
import WrappedDesktopBg from "@public/wrapped-desktop-bg.png";
import WrappedMetaimage from "@public/wrapped-metaimage.png";
import { generateDomainPath } from "@components/layout/shared/link-props";
import logo from "@public/logo-white.png";
import useOneLinkUrl from "hooks/useOneLinkUrl";
import { useWindowWidth } from "@react-hook/window-size";

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await loadTranslation(
    locale,
    process.env.NODE_ENV == "production"
  );

  return { props: { translation } };
};

const WrappedLanding = () => {
  const switchToApp = useOneLinkUrl("https://link.luminews.my/ewC9/qrdl2");
  const router = useRouter();
  const { locale } = router;
  const title = "Your 2025 Lumi News Wrapped is Here";
  const description =
    "Discover your Lumi News Wrapped, where readers dive into their top moments of the year. See how you spent time on Lumi News in 2025.";

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        noindex={false}
        nofollow={false}
        openGraph={{
          url: "https://luminews.my/wrapped",
          title: title,
          description: description,
          images: [
            {
              url: WrappedMetaimage.src,
              alt: title,
            },
          ],
        }}
      />

      <main className="wrapped-main relative px-6 pb-[200px] font-poppins text-white lg:px-[200px]">
        <style>
          {`
            .wrapped-main {
              background: url("${WrappedMobileBg.src}");
              background-size: cover;
            }

            @media (min-width: 768px) {
              .wrapped-main {
                background: url("${WrappedDesktopBg.src}");
                background-size: cover;
              }
            }

            .wrapped-main > div {
              max-width: 800px;
              margin: 0 auto;
            }
          `}
        </style>
        <Link href={generateDomainPath(`/${locale}`)}>
          <a className="lg:-inset-x-44150px] absolute mt-10">
            <Image src={logo} height={24} width={104} alt="logo" priority />
          </a>
        </Link>

        <div className="hero py-10 pt-[150px] lg:w-[600px]">
          <h1
            className="text-[45px] font-extrabold text-white"
            style={{ lineHeight: "60px" }}
          >
            Lumi News Wrapped 2025
          </h1>
          <p className="mt-5">
            Read news on the Lumi News app to unlock the full experience ✨
          </p>
          <a
            href={switchToApp}
            className="mt-5 block w-full cursor-pointer rounded-2xl p-4 text-center"
            style={{
              background: "rgba(40, 45, 66, 0.5)",
              backgroundBlendMode: "multiply",
            }}
          >
            Discover your Wrapped now
          </a>
        </div>

        <div className="mt-20 py-8 font-poppins lg:!mt-[100px] lg:w-[500px]">
          <h2
            className="text-center text-[24px] font-bold"
            style={{ lineHeight: "30px" }}
          >
            Get the latest version of Lumi News to unlock the experience
          </h2>
          <p className="mt-8 text-center text-[20px]">
            You’ll be notified when your Wrapped 🌯 is ready!
          </p>
        </div>

        <div className="!mt-20 pt-20 lg:mt-[200px] lg:!max-w-full">
          <h2
            className="text-center text-[36px] font-bold"
            style={{ lineHeight: "30px" }}
          >
            What is it?
          </h2>

          <div className="mt-8 flex flex-col lg:flex-row">
            <div className="flex flex-1 flex-col space-y-2 py-4 text-center lg:px-4">
              <span
                style={{ backgroundColor: "rgba(40, 45, 66, 0.5)" }}
                className="mx-auto flex h-[40px] w-[40px] flex-col justify-center rounded-full text-[20px] "
              >
                🏆
              </span>
              <p className="text-xl font-bold">A personal reflection</p>
              <p>
                Your achievements and even news personality type will all be
                revealed based on your reading habits!
              </p>
            </div>
            <div className="flex flex-1 flex-col space-y-2 py-4 text-center lg:px-4">
              <span
                style={{ backgroundColor: "rgba(40, 45, 66, 0.5)" }}
                className="mx-auto flex h-[40px] w-[40px] flex-col justify-center rounded-full text-[20px] "
              >
                ⏳
              </span>
              <p className="text-xl font-bold">A time machine</p>
              <p>
                Do you remember Xi Jin Ping &amp; Trump visiting Malaysia this
                year? Take a look at this year’s major events all in one trip
                down memory lane!
              </p>
            </div>
            <div className="flex flex-1 flex-col space-y-2 py-4 text-center lg:px-4">
              <span
                style={{ backgroundColor: "rgba(40, 45, 66, 0.5)" }}
                className="mx-auto flex h-[40px] w-[40px] flex-col justify-center rounded-full text-[20px] "
              >
                🫂
              </span>
              <p className="text-xl font-bold">A big thank you!</p>
              <p>We couldn’t have made it without your readership!</p>
            </div>
          </div>
        </div>

        <div className="mt-10 py-8 pt-20 font-poppins lg:!mt-[150px] lg:flex lg:!max-w-[800px]">
          <h2
            className="lg:flex-2 flex flex-col justify-center text-center text-[24px] font-bold"
            style={{ lineHeight: "30px" }}
          >
            <span>Can't find your Wrapped?</span>
          </h2>
          <p className="mt-8 text-center text-[16px] lg:ml-8 lg:flex-1 lg:text-left">
            Your Wrapped is only available once we get an idea of what kind of
            news reader you are! Open up a headline &amp; start catching up with
            the news to unlock the experience!
          </p>
        </div>

        <div className="mt-10 py-8 pt-20 font-poppins lg:!mt-[150px] lg:w-[600px]">
          <p className="text-center text-[20px]">
            Spotify may have done it first, but wouldn’t news-reading be funner
            if we did it too? 😝 Take a look at your year in review with Lumi
            News Wrapped 2025
          </p>
          <a
            href={switchToApp}
            className="mt-10 block w-full cursor-pointer rounded-2xl p-4 text-center"
            style={{
              background: "rgba(40, 45, 66, 0.5)",
              backgroundBlendMode: "multiply",
            }}
          >
            Discover your Wrapped now
          </a>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default WrappedLanding;
